import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Pencil, Loader2, Check, RotateCcw, Link2, ShieldAlert, Trash2 } from "lucide-react";
import { toast } from "sonner";

// Helm household editing: contact/Medicare profile, case status, MBI (vault), plans, links.
const FIELDS = [
  ["first_name", "First name"], ["last_name", "Last name"], ["phone", "Phone"], ["email", "Email"],
  ["date_of_birth", "Date of birth (YYYY-MM-DD)"], ["address_line1", "Street address"], ["city", "City"],
  ["state", "State"], ["zip_code", "ZIP"], ["county", "County"], ["medicaid_status", "Medicaid status"],
  ["part_a_effective_date", "Part A effective date (YYYY-MM-DD)"], ["part_b_effective_date", "Part B effective date (YYYY-MM-DD)"],
];
const MEDICAID = ["", "not_eligible", "unknown", "full_dual", "partial_dual_qmb", "partial_dual_slmb", "partial_dual_qi", "pending"];
const CASE = ["new_lead", "assessment_in_progress", "assessment_complete", "exploring_plans",
  "enrollment_submitted", "enrollment_pending", "enrolled", "member"];
const lbl = (s) => (s || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

export const SaveState = ({ state, onRetry, testid }) => (
  <span className="text-xs inline-flex items-center gap-1" data-testid={testid}>
    {state === "saving" && <><Loader2 className="w-3 h-3 animate-spin" />Saving…</>}
    {state === "saved" && <span className="text-[#047857] inline-flex items-center gap-1"><Check className="w-3 h-3" />Saved</span>}
    {state === "error" && <button type="button" onClick={onRetry} className="text-[#B91C1C] underline inline-flex items-center gap-1"><RotateCcw className="w-3 h-3" />Retry</button>}
  </span>
);

const Source = ({ s }) => s?.source === "agent"
  ? <span className="text-[10px] text-[#92400E]">agent · {s.name} · {(s.at || "").slice(0, 10)}</span>
  : <span className="text-[10px] text-[#6B7280]">consumer / intake</span>;

export function HouseholdProfileEditor({ hid, onSaved }) {
  const [p, setP] = useState(null);
  const [edit, setEdit] = useState(false);
  const [form, setForm] = useState({});
  const [state, setState] = useState(null);
  const [err, setErr] = useState(null);
  const load = useCallback(async () => {
    try { const { data } = await api.get(`/agent/households/${hid}/profile`); setP(data); setErr(null); }
    catch (e) { setErr(e?.response?.data?.detail || "Couldn't load this profile."); }
  }, [hid]);
  useEffect(() => { load(); }, [load]);
  const current = p ? { ...p.fields, ...(p.medicare || { medicaid_status: p.medicaid_status }) } : {};

  const save = async () => {
    const changes = {}, expected = {};
    Object.keys(form).forEach((k) => { if ((form[k] || "") !== (current[k] || "")) { changes[k] = form[k]; expected[k] = current[k] ?? null; } });
    if (!Object.keys(changes).length) { setEdit(false); return; }
    setState("saving"); setErr(null);
    try {
      await api.patch(`/agent/households/${hid}/profile`, { changes, expected });
      setState("saved"); setEdit(false); await load(); onSaved && onSaved();
    } catch (e) { setState("error"); setErr(e?.response?.data?.detail || "Save failed. Check your connection and retry."); }
  };
  const setStatus = async (status) => {
    setState("saving");
    try { await api.patch(`/agent/households/${hid}/status`, { status }); setState("saved"); await load(); }
    catch (e) { setState("error"); setErr(e?.response?.data?.detail || "Couldn't update case status."); }
  };

  if (!p) return <p className="text-sm text-[#6B7280] mt-4" data-testid="hp-loading">{err || "Loading profile…"}</p>;
  return (
    <div className="rounded-xl border-2 border-[#E5E5E0] bg-white p-5 mt-4" data-testid="hp-editor">
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <h3 className="font-bold text-[#0B192C]">Contact, Medicare &amp; eligibility details</h3>
        <SaveState state={state} onRetry={save} testid="hp-save-state" />
        <label className="ml-auto text-xs text-[#6B7280] flex items-center gap-1">Case status
          <select value={p.case_status || ""} onChange={(e) => setStatus(e.target.value)} className="h-8 rounded-md border px-1 text-xs" data-testid="hp-case-status">
            {!p.case_status && <option value="">Not set</option>}
            {CASE.map((s) => <option key={s} value={s}>{lbl(s)}</option>)}
          </select></label>
        {!edit && <Button size="sm" variant="outline" onClick={() => { setForm({ ...current }); setEdit(true); setState(null); }} data-testid="hp-edit-btn"><Pencil className="w-4 h-4 mr-1" />Edit</Button>}
      </div>
      {err && <p className="text-xs text-[#B91C1C] mb-2" data-testid="hp-error">{err} <button type="button" className="underline" onClick={load}>Reload</button></p>}
      <div className="grid sm:grid-cols-2 gap-3">
        {FIELDS.map(([k, label]) => (
          <div key={k}>
            <p className="text-xs text-[#6B7280]">{label}</p>
            {edit ? (k === "medicaid_status"
              ? <select value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="h-10 w-full rounded-md border px-2 text-sm" data-testid={`hp-input-${k}`}>{[...MEDICAID, ...(current[k] && !MEDICAID.includes(current[k]) ? [current[k]] : [])].map((m) => <option key={m} value={m}>{m ? lbl(m) : "Not recorded"}</option>)}</select>
              : <Input value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="h-10" data-testid={`hp-input-${k}`} />)
              : <p className="font-semibold text-[#0B192C] text-sm" data-testid={`hp-value-${k}`}>{(k === "medicaid_status" ? lbl(current[k]) : current[k]) || "—"}</p>}
            {!edit && current[k] && <Source s={p.field_sources?.[k]} />}
          </div>
        ))}
      </div>
      {edit && (
        <div className="mt-3 flex gap-2">
          <Button onClick={save} disabled={state === "saving"} className="bg-[#0B192C] text-white" data-testid="hp-save-btn">Save</Button>
          <Button variant="ghost" onClick={() => { setEdit(false); setErr(null); }} data-testid="hp-cancel-btn">Cancel</Button>
        </div>
      )}
      <IdentifiersCard hid={hid} pid={p.person_id} enabled={p.vault_enabled} />
      <LinksCard links={p.links} />
    </div>
  );
}

const ID_ROWS = [["mbi", "Medicare number (MBI)"], ["ssn", "Social Security number (optional)"], ["medicaid_number", "Medicaid number"]];

function IdentifiersCard({ hid, pid, enabled }) {
  const [ids, setIds] = useState(null);
  const [err, setErr] = useState(null);
  const load = useCallback(async () => {
    if (!enabled) return;
    try { const { data } = await api.get(`/secure-ids/households/${hid}/persons/${pid}/identifiers`); setIds(data.identifiers || []); setErr(null); }
    catch (e) { setErr(e?.response?.data?.detail || "Couldn't load protected identifiers."); }
  }, [hid, pid, enabled]);
  useEffect(() => { load(); }, [load]);
  if (!enabled) return (
    <p className="mt-4 text-xs rounded-md bg-[#FFF7ED] text-[#9A3412] p-2 flex gap-1.5" data-testid="hp-mbi-disabled">
      <ShieldAlert className="w-4 h-4 shrink-0" />Medicare number, SSN and Medicaid number: protected identifier storage is turned off in this environment, so they can't be saved here yet. Don't put them in notes or other fields.
    </p>
  );
  return (
    <div className="mt-4 rounded-md border p-3 space-y-3" data-testid="hp-identifiers">
      <p className="text-xs font-semibold uppercase tracking-wide text-[#1E3E62]">Protected identifiers · agent-only, encrypted, masked</p>
      {err && <p className="text-xs text-[#B91C1C]">{err}</p>}
      {ID_ROWS.map(([t, label]) => <IdRow key={t} hid={hid} pid={pid} t={t} label={label} rec={(ids || []).find((i) => i.id_type === t)} onSaved={load} />)}
    </div>
  );
}

function IdRow({ hid, pid, t, label, rec, onSaved }) {
  const [f, setF] = useState({ value: "", disclosed: false, state: "", unknown: false });
  const [state, setState] = useState(null);
  const [err, setErr] = useState(null);
  const [rv, setRv] = useState(null);
  const [shown, setShown] = useState(null);
  const base = `/secure-ids/households/${hid}/persons/${pid}/identifiers/${t}`;
  const ready = f.value && f.disclosed && (t !== "medicaid_number" || f.state) && (t !== "ssn" || f.unknown);
  const save = async () => {
    setState("saving"); setErr(null);
    const body = { value: f.value, authority: "beneficiary_verbal", storage_disclosed: f.disclosed,
      purpose: t === "ssn" ? "medicaid_lookup_fallback" : "medicare_servicing" };
    if (t === "ssn") body.medicaid_number_known = false;
    if (t === "medicaid_number") body.issuing_state = f.state.toUpperCase();
    try { await api.put(base, body); setF({ value: "", disclosed: false, state: "", unknown: false }); setState("saved"); onSaved(); }
    catch (e) { setState("error"); setErr(e?.response?.data?.detail || "Couldn't save."); }
  };
  const reveal = async () => {
    try { const { data } = await api.post(`${base}/reveal`, { password: rv.password, purpose: rv.purpose }); setShown(data.value); setRv(null); setTimeout(() => setShown(null), 30000); }
    catch (e) { setErr(e?.response?.data?.detail || "Reveal denied."); }
  };
  return (
    <div className="border-t pt-2" data-testid={`hp-id-${t}`}>
      <p className="text-xs text-[#6B7280]">{label}</p>
      <p className="font-semibold text-sm" data-testid={`hp-id-masked-${t}`}>{shown || (rec ? `${rec.masked}${rec.issuing_state ? ` · ${rec.issuing_state}` : ""}` : "Not on file")}</p>
      {rec && !shown && !rv && <button type="button" className="text-xs underline text-[#1E3E62]" onClick={() => setRv({ password: "", purpose: "" })} data-testid={`hp-id-reveal-${t}`}>Reveal (password + reason, audited)</button>}
      {rv && (
        <div className="flex flex-wrap gap-2 mt-1">
          <Input type="password" autoComplete="current-password" placeholder="Your password" value={rv.password} onChange={(e) => setRv({ ...rv, password: e.target.value })} className="h-8 w-40" data-testid={`hp-id-reveal-pw-${t}`} />
          <Input placeholder="Reason" value={rv.purpose} onChange={(e) => setRv({ ...rv, purpose: e.target.value })} className="h-8 w-40" data-testid={`hp-id-reveal-purpose-${t}`} />
          <Button size="sm" disabled={!rv.password || !rv.purpose} onClick={reveal} data-testid={`hp-id-reveal-go-${t}`}>Reveal</Button>
          <Button size="sm" variant="ghost" onClick={() => setRv(null)}>Cancel</Button>
        </div>
      )}
      <div className="mt-1 flex flex-wrap gap-2 items-center">
        <Input type="password" autoComplete="off" value={f.value} onChange={(e) => setF({ ...f, value: e.target.value })} placeholder={rec ? "Replace" : "Enter"} className="h-8 w-40" data-testid={`hp-id-input-${t}`} />
        {t === "medicaid_number" && <Input value={f.state} onChange={(e) => setF({ ...f, state: e.target.value })} placeholder="State" maxLength={2} className="h-8 w-16" data-testid="hp-id-state-medicaid_number" />}
        {t === "ssn" && <label className="text-xs flex items-center gap-1"><input type="checkbox" checked={f.unknown} onChange={(e) => setF({ ...f, unknown: e.target.checked })} data-testid="hp-id-ssn-unknown" />Medicaid number unknown; SSN used only for the Medicaid lookup</label>}
        <label className="text-xs flex items-center gap-1"><input type="checkbox" checked={f.disclosed} onChange={(e) => setF({ ...f, disclosed: e.target.checked })} data-testid={`hp-id-disclosed-${t}`} />Client told it will be stored</label>
        <Button size="sm" onClick={save} disabled={!ready || state === "saving"} data-testid={`hp-id-save-${t}`}>Save</Button>
        <SaveState state={state} onRetry={save} testid={`hp-id-state-${t}`} />
      </div>
      {err && <p className="text-xs text-[#B91C1C] mt-1">{err}</p>}
    </div>
  );
}

function LinksCard({ links }) {
  const Row = ({ label, url, testid }) => (
    <p className="text-sm" data-testid={testid}><Link2 className="w-3.5 h-3.5 inline mr-1" />{label}: {url
      ? <a href={url} target="_blank" rel="noreferrer" className="underline text-[#1E3E62]">Open</a>
      : <span className="text-[#6B7280]">not configured for this rep</span>}</p>
  );
  return (
    <div className="mt-4 space-y-1" data-testid="hp-links">
      <Row label="Sunfire marketplace link" url={links?.sunfire_purl} testid="hp-link-sunfire" />
      <Row label="Scope of Appointment (SOA) link" url={links?.soa_url} testid="hp-link-soa" />
    </div>
  );
}

export function CandidatePlansEditor({ hid, cands, onChanged }) {
  const [busy, setBusy] = useState(null);
  const remove = async (id) => {
    setBusy(id);
    try { await api.delete(`/agent/households/${hid}/candidates/${encodeURIComponent(id)}`); toast.success("Plan removed from consideration."); onChanged(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't remove that plan. Retry."); }
    finally { setBusy(null); }
  };
  return (
    <div className="mt-4" data-testid="profile-candidate-plans">
      <p className="text-xs font-semibold uppercase tracking-wide text-[#1E3E62] mb-2">Plans under consideration</p>
      {cands.length === 0 && <p className="text-xs text-[#6B7280]">None yet.</p>}
      {cands.map((c) => (
        <div key={c.display_id} className="text-xs text-[#1E3E62] py-1 border-t border-[#F0EEE8] flex items-center gap-2" data-testid={`profile-candidate-${c.display_id}`}>
          <span className="font-semibold">{c.plan_name || c.display_id}</span> · {c.display_id} · {c.plan_year}
          <button type="button" disabled={busy === c.display_id} onClick={() => remove(c.display_id)} className="ml-auto text-[#991B1B] underline" data-testid={`candidate-remove-${c.display_id}`}><Trash2 className="w-3 h-3 inline" /> Remove</button>
        </div>
      ))}
      <p className="text-[11px] text-[#6B7280] mt-1">To add plans, use Compare in the Ask Nauticus tab.</p>
    </div>
  );
}
